<template>
  <div class="oai-BillingStep">
    <DefineTemplate>
      <AppButtonNext
        class="oai-BillingStep_FooterBtn"
        :disabled="isNavigating"
        variant="secondary-gray"
        @click="onSkip"
      >
        {{ $t('action.skip') }}
      </AppButtonNext>
    </DefineTemplate>

    <div v-if="!loading" class="oai-BillingStep_Wrapper">
      <div class="oai-BillingStep_Cta">
        <i18n-t
          keypath="onboarding.ai.billing.title_prefix"
          scope="global"
          tag="h1"
          class="oai-StepHeader_Title"
        >
          <template #unlock>
            <span class="oai-StepHeader_TitleAccent">
              {{ $t('onboarding.ai.billing.title_accent') }}
            </span>
          </template>
        </i18n-t>
      </div>

      <SubscriptionTiers
        :status-data="statusData"
        :subscription="subscription"
        @on-contact-us="onContactUs"
        @on-subscribe="onSubscribeTiers"
      >
        <template #feature-list:actions="{ showFeatureLists, onComparePlans }">
          <div v-if="!showFeatureLists" class="oai-BillingStep_Header">
            <ReuseTemplate />
            <AppButtonNext
              :variant="BUTTON_VARIANTS.SECONDARY_GRAY"
              class="oai-BillingStep_FooterBtn"
              @click="onComparePlansLocal(onComparePlans)"
            >
              {{ $t('subscription.compare_plans') }}
            </AppButtonNext>
          </div>
        </template>
      </SubscriptionTiers>
    </div>

    <div v-if="showSkipButton" class="oai-BillingStep_Footer">
      <ReuseTemplate />
    </div>

    <portal to="modal-windows">
      <ManagePlanDialog
        v-model:chosen-subscription="managePlanPayload"
        v-model:show="isShowManagePlan"
        :before-proceed-to-checkout="completeOnboarding"
        :current-subscription="subscription"
        :billing-redirect-id="BILLING_REDIRECT_ID.HOME"
        with-switch-plan
      />
      <BillingContactUsDialog v-model:show="isShowBillingContactDialog" />
    </portal>
  </div>
</template>

<script setup>
import { createReusableTemplate } from '@vueuse/core'
import { computed } from 'vue'
import { ref } from 'vue'

import {
  useOnboardingAiBillingComplete,
  useOnboardingAiStepContext
} from '@/composables/onboarding-ai-step'
import { useOnboardingAiTracking } from '@/composables/onboarding-ai-step'
import { EVENT_NAMES } from '@/tracking/amplitude-helpers'
import { BILLING_REDIRECT_ID, useManagePlanDialog, useSubscription } from '@/utils/billing-settings'
import { BUTTON_VARIANTS } from '@/utils/components-configurations/app-button'
import BillingContactUsDialog from '@shared-modules/components/billing-settings/BillingContactUsDialog'
import ManagePlanDialog from '@shared-modules/components/billing-settings/ManagePlanDialog'
import SubscriptionTiers from '@shared-modules/components/billing-settings/SubscriptionTiers'

import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'

defineOptions({
  name: 'OnboardingAiBillingStep'
})

const { logEvent } = useOnboardingAiTracking()

const [DefineTemplate, ReuseTemplate] = createReusableTemplate()

const stepContext = useOnboardingAiStepContext()
const completeOnboarding = useOnboardingAiBillingComplete()
const isNavigating = computed(() => Boolean(stepContext?.navigating))

const { subscription, statusData, loading } = useSubscription({
  trackCallback: (payload = {}) => {
    logEvent(EVENT_NAMES.ONBOARDING_PAYWALL_VIEWED, payload)
  }
})

const {
  onSubscribe,
  onContactUs,
  isShowBillingContactDialog,
  isShowManagePlan,
  managePlanPayload
} = useManagePlanDialog({
  subscribeTrackCallback: (payload = {}) => {
    logEvent(EVENT_NAMES.ONBOARDING_PAYWALL_PLAN_CLICKED, {
      plan: payload.name.toLowerCase(),
      billing_cycle: payload.period
    })
  },
  contactUsTrackCallback: (payload = {}) => {
    logEvent(EVENT_NAMES.ONBOARDING_PAYWALL_ENTERPRISE_CONTACT_CLICKED)
    logEvent(EVENT_NAMES.ONBOARDING_PAYWALL_PLAN_CLICKED, {
      plan: payload.name.toLowerCase(),
      billing_cycle: payload.period
    })
  }
})

const onSubscribeTiers = payload => {
  onSubscribe(payload)
}

const showSkipButton = ref(false)

const onComparePlansLocal = (callback = () => {}) => {
  showSkipButton.value = true
  callback?.()
}

const onSkip = async () => {
  await completeOnboarding({
    callback: () => {
      logEvent(EVENT_NAMES.ONBOARDING_PAYWALL_SKIP_CLICKED)
    }
  })
}
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/onboarding-ai-step';

.oai-BillingStep {
  --step-max-width: 1076px;
  width: 100%;
  max-width: var(--step-max-width);
  margin-inline: auto;
}

.oai-BillingStep_Wrapper {
  --ob-fl-feature-list-header-title-background-color: #{$oai-global-bg};
  --ob-fl-feature-list-header-title-top: -#{$oai-flow-padding-top};
  --ob-si-background-color: #{$white};
}

.oai-BillingStep_Cta {
  margin-bottom: 32px;

  .oai-StepHeader_Title {
    text-align: center;
  }
}

.oai-BillingStep_Header {
  display: flex;
  justify-content: center;
  gap: 16px;
}

.oai-BillingStep_Footer {
  display: flex;
  justify-content: center;
  gap: 16px;
  position: sticky;
  bottom: -#{$oai-flow-padding-bottom};
  z-index: 2;
  margin-top: 24px;
  padding: 16px 0 24px;
  background: linear-gradient(180deg, rgba($oai-global-bg, 0) 0%, $oai-global-bg 40%);
}

.oai-BillingStep_FooterBtn {
  font-weight: fw('semi-bold');
}
</style>
